import React from "react"
import { BrowserRouter, useLocation, useRoutes } from "react-router-dom"

import { useAppState, useReaction } from "src/state"

import { Loader } from "src/components/global/Loader"
import { NavContext, NavContextType } from "src/components/navigation/NavContext"

import { User_Roles_Enum, User_Type_Enum } from "src/state/gql/_types"
import { UnverifiedRoutes } from "src/routes/UnverifiedRoutes"

import { GuestRoutes } from 'src/routes/GuestRoutes'
import { SuperAdminRoutes } from "src/routes/SuperAdminRoutes"
import { OperationsAdminRoutes } from "src/routes/OperationsAdminRoutes"
import { TalentAdminRoutes } from "src/routes/TalentAdminRoutes"
import { AgencyOperationsAdminRoutes } from "src/routes/AgencyOperationsAdminRoutes"

type AppRole = User_Roles_Enum | 'unverified' | 'agency_operations_admin';

function resolveRoutes(role: AppRole | null, userType: User_Type_Enum | null): NavContextType {
  if (!role) return GuestRoutes();
  if (role === 'unverified') return UnverifiedRoutes();
  if (role === User_Roles_Enum.Guest) return GuestRoutes();
  if (role === User_Roles_Enum.Superadmin) return SuperAdminRoutes();
  if (role === User_Roles_Enum.AgencyOperationsAdministrator) return AgencyOperationsAdminRoutes();
  if (role === User_Roles_Enum.AgencyTalentAdministrator) return TalentAdminRoutes();
  if (role === User_Roles_Enum.VenueOperationsAdministrator) return OperationsAdminRoutes();

  if (role === User_Roles_Enum.Administrator) {
    if (userType === User_Type_Enum.Operations) 
        return OperationsAdminRoutes();
    if (userType === User_Type_Enum.Talent) 
        return TalentAdminRoutes();
    return GuestRoutes(); // explicit fallback
  }

  return GuestRoutes();
}

type LocationState = {
    drawerFrom?: Location
} | undefined

const NavigationRoutes = () => {

    const location = useLocation();
    const { loading } = useAppState(({ loading, profile }) => ({ loading, profile }))
    const { routes, overlays } = React.useContext(NavContext)

    const state = location.state as LocationState;
    const hasBackground = !!state?.drawerFrom;

    const background = useRoutes(routes, state?.drawerFrom ?? location);
    const overlay    = useRoutes(overlays || [], location);

    if (loading) return <Loader />

    return (
        <>
            {background}
            {hasBackground ? overlay : null}
        </>
    )

}

export const NavigationWrapper: React.FC = () => {

    let [routes, setRoutes] = React.useState<NavContextType | null>(null)
    const [loading, setLoading] = React.useState(useAppState().loading)

    const reaction = useReaction()

    React.useEffect(() => {
        const dispose = reaction(
            (state) => state.loading,
            (newLoading) => setLoading(newLoading),
            { immediate: true }
        )
        return dispose
    }, [reaction])

    React.useEffect(() => {
        const dispose = reaction(
            (state) => ({ user_role: state.auth.user_role, user_type: state.auth.user_type }),
            ({ user_role, user_type }) => {

                setRoutes(resolveRoutes(user_role, user_type))

            },
            { immediate: true }
        )
        return dispose
    }, [reaction])

    if (loading) return <Loader />
    if (!routes) return <div>No routes</div>

    return (
        <NavContext.Provider value={routes}>
            <BrowserRouter>
                <NavigationRoutes />
            </BrowserRouter>
        </NavContext.Provider>
    )

}
